import { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, X, Send, Minimize2 } from 'lucide-react';
import { env } from '@/config/env';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------
interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
}

interface ApiHistoryItem {
    role: 'user' | 'assistant';
    content: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function formatTime(date: Date): string {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

// ---------------------------------------------------------------------------
// Subcomponentes
// ---------------------------------------------------------------------------

/** Animação de "digitando" com três bolinhas pulsantes */
function TypingIndicator() {
    return (
        <div className="flex items-end gap-2 mb-3">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-sm">
                <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                <div className="flex items-center gap-1">
                    <span
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0ms', animationDuration: '1s' }}
                    />
                    <span
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: '200ms', animationDuration: '1s' }}
                    />
                    <span
                        className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                        style={{ animationDelay: '400ms', animationDuration: '1s' }}
                    />
                </div>
            </div>
        </div>
    );
}

/** Balão de mensagem individual */
function MessageBubble({ message }: { message: Message }) {
    const isUser = message.role === 'user';

    if (isUser) {
        return (
            <div className="flex justify-end mb-3">
                <div className="max-w-[80%]">
                    <div className="bg-gradient-to-br from-violet-600 to-indigo-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 shadow-sm">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                            {message.content}
                        </p>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1 text-right pr-1">
                        {formatTime(message.timestamp)}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-end gap-2 mb-3">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-sm">
                <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="max-w-[80%]">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm">
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                        {message.content}
                    </p>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 pl-1">
                    {formatTime(message.timestamp)}
                </p>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
const INITIAL_MESSAGE: Message = {
    id: 'init',
    role: 'assistant',
    content: 'Olá! Sou o AlphaBot, assistente virtual da AlphaLabs. Como posso ajudar você hoje?\n\nPosso falar sobre nossos serviços, preços, tecnologias e muito mais.',
    timestamp: new Date(),
};

export default function Chatbot() {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [hasNewMessage, setHasNewMessage] = useState(true);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Scroll automático ao receber mensagem
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // Foca o input ao abrir
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => inputRef.current?.focus(), 150);
            setHasNewMessage(false);
        }
    }, [isOpen]);

    const sendMessage = useCallback(async () => {
        const trimmed = input.trim();
        if (!trimmed || isTyping) return;

        const userMessage: Message = {
            id: generateId(),
            role: 'user',
            content: trimmed,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsTyping(true);

        // Histórico para a API (sem a mensagem inicial fixa)
        const historyForApi: ApiHistoryItem[] = messages
            .filter(m => m.id !== 'init')
            .map(m => ({ role: m.role, content: m.content }));

        try {
            const response = await fetch(`${env.API_URL.replace(/\/api$/, '')}/api/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: trimmed, history: historyForApi }),
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error((err as { message?: string }).message || `Erro ${response.status}`);
            }

            const data = await response.json();
            const assistantMessage: Message = {
                id: generateId(),
                role: 'assistant',
                content: data.data?.message ?? 'Não consegui processar sua mensagem.',
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, assistantMessage]);
            if (!isOpen) setHasNewMessage(true);
        } catch (error) {
            const errText = error instanceof Error ? error.message : 'Erro desconhecido';
            setMessages(prev => [
                ...prev,
                {
                    id: generateId(),
                    role: 'assistant',
                    content: `Desculpe, ocorreu um erro: ${errText}\n\nTente novamente ou fale conosco em contato@alphalabs.fyi`,
                    timestamp: new Date(),
                },
            ]);
        } finally {
            setIsTyping(false);
        }
    }, [input, isTyping, messages, isOpen]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
    };

    return (
        <>
            {/* Janela do chat */}
            {isOpen && (
                <div
                    className="fixed bottom-20 right-5 z-50 w-[320px] max-w-[calc(100vw-2rem)] flex flex-col rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    style={{ height: '420px' }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 flex-shrink-0">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                <Bot className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <p className="text-white font-semibold text-sm leading-tight">AlphaBot</p>
                                <div className="flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                    <p className="text-white/80 text-xs">Online agora</p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
                            aria-label="Minimizar chat"
                        >
                            <Minimize2 className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Área de mensagens */}
                    <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50 dark:bg-gray-900">
                        {messages.map(msg => (
                            <MessageBubble key={msg.id} message={msg} />
                        ))}
                        {isTyping && <TypingIndicator />}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input fixo no rodapé */}
                    <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-3 py-3">
                        <div className="flex items-end gap-2">
                            <textarea
                                ref={inputRef}
                                value={input}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Digite sua mensagem..."
                                rows={1}
                                disabled={isTyping}
                                className="flex-1 resize-none rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent placeholder-gray-400 disabled:opacity-50 transition-all"
                                style={{ minHeight: '40px', maxHeight: '120px' }}
                            />
                            <button
                                onClick={sendMessage}
                                disabled={!input.trim() || isTyping}
                                className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white shadow-sm hover:shadow-md hover:from-violet-700 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                                aria-label="Enviar mensagem"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                        <p className="text-[10px] text-gray-400 text-center mt-1.5">
                            Powered by AlphaLabs AI · Enter para enviar
                        </p>
                    </div>
                </div>
            )}

            {/* Botão flutuante com ping de notificação */}
            <button
                onClick={() => setIsOpen(prev => !prev)}
                className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200"
                aria-label={isOpen ? 'Fechar chat' : 'Abrir chat'}
            >
                {hasNewMessage && !isOpen && (
                    <>
                        <span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white z-10" />
                        <span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-red-400 animate-ping" />
                    </>
                )}
                {isOpen ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}
            </button>
        </>
    );
}
